/** PaymentIntent statuses that mean the money is secured and the express saga may resume:
 *  book with the supplier, then capture (or void on failure).
 *  - `requires_capture` — authorized under manual capture. The normal outcome for every method.
 *  - `succeeded`        — auto-captured up front (fallback for anything not on manual capture).
 *  - `processing`       — the method authorizes asynchronously and Stripe has not settled yet.
 *                         Only reached after {@link waitWhileProcessing} gives up waiting; the BFF
 *                         handles this status rather than treating it as a failure. */
const AUTHORIZED_STATUSES = ['requires_capture', 'succeeded', 'processing'];

/**
 * True when `status` means the payment was authorized and the booking may be resumed. Anything else
 * (a decline, or a buyer cancellation, which resolves with no error and the intent back in
 * `requires_payment_method`) must resume with `isCancel` so the pending express booking is voided.
 */
export function isStripeExpressAuthorized(status: string | undefined | null): boolean {
  return !!status && AUTHORIZED_STATUSES.includes(status);
}

/**
 * True when the customer simply has not finished the action yet.
 *
 * `requires_action` is neither authorized nor declined. The case that matters is Cash App Pay: closing
 * its QR modal leaves the intent here with the authorization session still live for about an hour, so
 * the customer may be approving it on their phone at that very moment. Resuming with `isCancel` would
 * void a payment that is still in flight — which is what TRAV23-4560 warned against, and what surfaced
 * as a 400 from `book-resume-express` in TRAV23-4969. Leave the backend alone and let the customer
 * pick again; abandoned contexts are the server-side sweeper's job, not ours.
 *
 * A genuine buyer cancellation is different — Amazon Pay's BuyerCanceled comes back as
 * `requires_payment_method`, which is a decline and still has to cancel.
 */
export function isStripeExpressPending(status: string | undefined | null): boolean {
  return status === 'requires_action';
}

/** How long to keep polling a `processing` intent before giving up and resuming anyway. */
const PROCESSING_POLL_TIMEOUT_MS = 60_000;
const PROCESSING_POLL_INTERVAL_MS = 2_000;

const delay = (ms: number) => new Promise((resolve) => setTimeout(resolve, ms));

/**
 * Redirect / QR methods (Klarna, Afterpay/Clearpay, Cash App Pay) can settle asynchronously: the
 * customer has authorized but Stripe reports `processing` for a few seconds before the intent
 * reaches `requires_capture`. Resuming the express booking in that window looks like a payment
 * failure and rolls the booking back, so poll the intent until it leaves `processing`.
 */
async function waitWhileProcessing(
  stripe: any,
  clientSecret: string,
  paymentIntent: { status?: string; client_secret?: string } | undefined,
): Promise<{ status?: string; client_secret?: string } | undefined> {
  if (paymentIntent?.status !== 'processing' || typeof stripe?.retrievePaymentIntent !== 'function') {
    return paymentIntent;
  }
  const secret = paymentIntent.client_secret ?? clientSecret;
  const deadline = Date.now() + PROCESSING_POLL_TIMEOUT_MS;
  let latest = paymentIntent;
  while (Date.now() < deadline) {
    await delay(PROCESSING_POLL_INTERVAL_MS);
    const retrieved = await stripe.retrievePaymentIntent(secret);
    if (retrieved?.error || !retrieved?.paymentIntent) {
      return latest;
    }
    latest = retrieved.paymentIntent;
    if (latest?.status !== 'processing') {
      return latest;
    }
  }
  return latest;
}

/** Billing details Stripe requires to create the PaymentMethod for a direct-confirm method.
 *  Afterpay/Clearpay needs name, email and a full address; Cash App Pay needs none of it. We source
 *  them from the same per-engine data the booking payload is built from. */
export interface StripeDirectConfirmBillingDetails {
  name?: string;
  email?: string;
  phone?: string;
  address?: {
    line1?: string;
    city?: string;
    state?: string;
    postal_code?: string;
    country?: string;
  };
}

/** What a component hands the booking thunks when the customer picked a direct-confirm method. */
export interface StripeDirectConfirm {
  /** Stripe `payment_method_types` token — `afterpay_clearpay` or `cashapp`. */
  paymentMethodType: string;
  billingDetails?: StripeDirectConfirmBillingDetails;
}

/** Methods confirmed through their own `stripe.confirmXxxPayment` API rather than through Elements.
 *  Stripe's ExpressCheckoutElement has no key for either, so there is no express button to render —
 *  we draw our own branded button and call these directly, which still gives Stripe's native redirect
 *  (Afterpay) or QR / redirect (Cash App Pay). Klarna is NOT here: it is a real ExpressCheckoutElement
 *  method and goes through the wallet path. See TRAV23-4560. */
const DIRECT_CONFIRM_METHODS = ['afterpay_clearpay', 'cashapp'];

export function isStripeDirectConfirmMethod(method: string | undefined | null): boolean {
  return !!method && DIRECT_CONFIRM_METHODS.includes(method);
}

/** Calls the method-specific confirm API. `handleActions` is left at its default (true) so Stripe
 *  owns the flow: a full-page redirect to Afterpay's site (the customer comes back via `return_url`),
 *  or the QR modal / app deep-link for Cash App Pay. */
async function confirmDirectMethod(
  stripe: any,
  method: string,
  clientSecret: string,
  returnUrl: string,
  billingDetails?: StripeDirectConfirmBillingDetails,
): Promise<{paymentIntent?: any; error?: {message?: string}}> {
  const data = {
    payment_method: billingDetails ? {billing_details: billingDetails} : undefined,
    return_url: returnUrl,
  };
  if (method === 'afterpay_clearpay') {
    return stripe.confirmAfterpayClearpayPayment(clientSecret, data);
  }
  return stripe.confirmCashappPayment(clientSecret, data);
}

/**
 * Confirms the express PaymentIntent and waits until its status is meaningful.
 *
 * Two confirm paths:
 *  - **Elements** (card, and the wallet ExpressCheckoutElement): {@link Stripe.confirmPayment} with
 *    `redirect: 'if_required'`.
 *  - **Direct** (Afterpay/Clearpay, Cash App Pay — see {@link isStripeDirectConfirmMethod}): the
 *    method's own `stripe.confirmXxxPayment`, which needs no Elements. Afterpay redirects the tab to
 *    its own site and returns via `return_url`; Cash App Pay shows a QR modal or deep-links the app.
 *
 * Either way we then call {@link Stripe.handleNextAction} while the intent is `requires_action`, so
 * authentication (3D Secure, or Cash App Pay's desktop QR) happens in an embedded flow where Stripe
 * supports it, and finally poll a `processing` intent until it settles — see {@link waitWhileProcessing}.
 *
 * Redirect-based payment methods may still perform a full navigation to `return_url`.
 */
export async function confirmPaymentWithEmbeddedNextActions(
  stripe: any,
  params: {
    elements?: unknown;
    clientSecret: string;
    returnUrl: string;
    /** Stripe `payment_method_types` token. Only consulted for the direct-confirm methods; anything
     *  else (card, wallets) goes through Elements as before. */
    paymentMethodType?: string;
    billingDetails?: StripeDirectConfirmBillingDetails;
  },
): Promise<{
  paymentIntent?: {status?: string; client_secret?: string};
  error?: {message?: string};
}> {
  const {elements, clientSecret, returnUrl, paymentMethodType, billingDetails} = params;

  let result = isStripeDirectConfirmMethod(paymentMethodType)
    ? await confirmDirectMethod(
        stripe,
        paymentMethodType as string,
        clientSecret,
        returnUrl,
        billingDetails,
      )
    : await stripe.confirmPayment({
        elements,
        clientSecret,
        confirmParams: {
          return_url: returnUrl,
        },
        redirect: 'if_required',
      });

  const maxNextActions = 8;
  for (let i = 0; i < maxNextActions; i++) {
    if (result?.error) {
      break;
    }
    if (result?.paymentIntent?.status !== 'requires_action') {
      break;
    }
    const pendingActionType = result.paymentIntent?.next_action?.type;
    const secret = result.paymentIntent?.client_secret ?? clientSecret;
    const next = await stripe.handleNextAction({clientSecret: secret});
    if (next?.error) {
      return {error: next.error, paymentIntent: undefined};
    }
    result = {
      paymentIntent: next.paymentIntent,
      error: undefined,
    };
    // Handing back the SAME action means the customer dismissed it rather than completing it.
    // Cash App Pay is the case that matters: closing its QR modal does not fail the intent — the
    // authorization session stays live for an hour — so the status is still `requires_action` and the
    // next action is unchanged. Looping here would just reopen the modal the customer closed, which is
    // what made it look like it reappeared forever (TRAV23-4874). Stop instead, and let the caller
    // treat it as not authorized so the booking is voided and the picker comes back.
    //
    // A genuinely new action (a different `type`) still loops, which is what this retry was for.
    if (
      result.paymentIntent?.status === 'requires_action' &&
      result.paymentIntent?.next_action?.type === pendingActionType
    ) {
      break;
    }
  }

  if (result?.error) {
    return {paymentIntent: result?.paymentIntent, error: result.error};
  }

  return {
    paymentIntent: await waitWhileProcessing(stripe, clientSecret, result?.paymentIntent),
    error: undefined,
  };
}
